import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  DatePicker,
  Drawer,
  Input,
  InputNumber,
  Popconfirm,
  Spin,
  Table,
  Tag,
  message,
} from "antd";
import { HistoryOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { Button } from "../../../../components/Component";
import {
  rateParameterApis,
  SaveRateParameterVersionPayload,
} from "../../../../api/MachineryEquipment";

/**
 * The shared rate card for a scope — EFFECTIVE-DATED: fuel and power prices,
 * the ten crew monthly wages, and the fallbacks for percentages an equipment
 * version leaves blank.
 *
 * Saving INSERTS A NEW CARD VERSION effective from the chosen instant — it
 * never edits the current one. Because the hourly heads are computed on read,
 * every machine re-rates from that instant automatically; costs incurred
 * BEFORE it stay on the old card (that is the whole point of requirement 2).
 * Back-dating is allowed and deliberately recomputes history.
 *
 * RESOLUTION IS WHOLE-CARD: a project chain REPLACES the organization chain,
 * it does not patch it field by field. The drawer shows which chain the
 * numbers on screen came from, and offers the org card as a starting point
 * when the project has no chain of its own.
 */

const WAGE_FIELDS: Array<[keyof SaveRateParameterVersionPayload, string, string]> = [
  ["wageForeman", "Foreman", "Skilled"],
  ["wageOperator", "Operator", "Skilled"],
  ["wageMechanic", "Mechanic", "Skilled"],
  ["wageElectrician", "Electrician", "Skilled"],
  ["wageSupervisor", "Supervisor", "Skilled"],
  ["wageDriver", "Driver", "Skilled"],
  ["wageCableman", "Cableman", "Skilled"],
  ["wageHelper", "Helper", "Unskilled"],
  ["wageWatchman", "Watchman", "Unskilled"],
  ["wageBeldar", "Beldar", "Unskilled"],
];

/** Stored as fractions, edited as percent. Converted only here. */
const PERCENT_FIELDS: Array<[keyof SaveRateParameterVersionPayload, string, string]> = [
  ["defaultScrapValuePct", "Scrap Value", "Typically 10%"],
  ["defaultInterestRatePct", "Interest Rate", "Per annum, typically 9.5%"],
  ["defaultSundriesPctOfRepair", "Sundries", "Of the repair cost, typically 5%"],
];

const toPercent = (value: any): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n * 100 * 1e6) / 1e6 : null;
};

const toNumber = (value: any): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const money = (value: any): string => {
  const n = toNumber(value);
  return n === null ? "—" : n.toLocaleString("en-IN");
};

type FormState = Record<string, number | null>;

const RateParametersDrawer = ({
  open,
  projectId = 0,
  projectName = "",
  onClose,
  onSaved,
}: {
  open: boolean;
  projectId?: number;
  /** Display name for the scope tag — the project title, never a raw id. */
  projectName?: string;
  onClose: () => void;
  onSaved?: () => void;
}) => {
  const [form, setForm] = useState<FormState>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  /** PROJECT | ORG | NONE — where the resolved numbers on screen came from. */
  const [resolvedFrom, setResolvedFrom] = useState("");
  const [orgCard, setOrgCard] = useState<any>(null);
  /** The scope's OWN version chain, newest first. */
  const [history, setHistory] = useState<any[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  const [effectiveFrom, setEffectiveFrom] = useState<Dayjs>(dayjs());
  const [remarks, setRemarks] = useState("");

  const hydrate = useCallback((card: any) => {
    if (!card) {
      setForm({});
      return;
    }
    const next: FormState = {
      dieselPricePerLitre: toNumber(card.dieselPricePerLitre),
      electricityRatePerKwh: toNumber(card.electricityRatePerKwh),
    };
    for (const [key] of WAGE_FIELDS) next[key] = toNumber(card[key]);
    for (const [key] of PERCENT_FIELDS) next[key] = toPercent(card[key]);
    setForm(next);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, historyRes] = await Promise.all([
        rateParameterApis.fetch(projectId),
        rateParameterApis.fetchHistory(projectId),
      ]);
      hydrate(res?.data?.parameters || null);
      setResolvedFrom(res?.data?.resolvedFrom || "NONE");
      setHistory(historyRes?.data?.versions || []);

      // Offer the org card as a starting point when this project has no chain.
      if (projectId > 0 && !(historyRes?.data?.versions || []).length) {
        const orgRes = await rateParameterApis.fetch(0);
        setOrgCard(orgRes?.data?.exists ? orgRes.data.parameters : null);
      } else {
        setOrgCard(null);
      }
    } finally {
      setLoading(false);
    }
  }, [projectId, hydrate]);

  useEffect(() => {
    if (open) {
      setEffectiveFrom(dayjs());
      setRemarks("");
      setShowHistory(false);
      load();
    }
  }, [open, load]);

  const set = (key: string, value: number | null) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!effectiveFrom) {
      message.error("Pick when this card takes effect.");
      return;
    }
    setSaving(true);
    try {
      const payload: SaveRateParameterVersionPayload = {
        projectId,
        effectiveFrom: effectiveFrom.toISOString(),
        remarks: remarks.trim() || undefined,
      };
      payload.dieselPricePerLitre = form.dieselPricePerLitre ?? undefined;
      payload.electricityRatePerKwh = form.electricityRatePerKwh ?? undefined;
      for (const [key] of WAGE_FIELDS) {
        (payload as any)[key] = form[key] ?? undefined;
      }
      // percent -> fraction, the one conversion boundary
      for (const [key] of PERCENT_FIELDS) {
        const v = form[key];
        (payload as any)[key] = v === null || v === undefined ? undefined : v / 100;
      }

      const res = await rateParameterApis.saveVersion(payload);
      if (res?.status) {
        message.success(res?.data?.message || "New card version saved.");
        onSaved?.();
        onClose();
      } else {
        message.error(res?.data?.message || res?.message || "Could not save.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleUndoLatest = async (record: any) => {
    const res = await rateParameterApis.deleteLatestVersion(
      projectId,
      record.rateParameterId
    );
    if (res?.status) {
      message.success(res?.data?.message || "Latest version removed.");
      onSaved?.();
      load();
    } else {
      message.error(res?.data?.message || res?.message || "Could not remove.");
    }
  };

  const numberField = (
    key: string,
    label: string,
    hint?: string,
    addon?: string
  ) => (
    <div className="col-md-6 mb-3" key={key}>
      <label className="form-label mb-1">{label}</label>
      <InputNumber
        value={form[key] as number}
        onChange={(v) => set(key, v as number)}
        style={{ width: "100%" }}
        min={0}
        addonAfter={addon}
        placeholder="—"
      />
      {hint ? (
        <div className="text-soft" style={{ fontSize: 11, marginTop: 2 }}>
          {hint}
        </div>
      ) : null}
    </div>
  );

  const historyColumns: any[] = [
    {
      title: "Effective From",
      dataIndex: "effectiveFrom",
      key: "effectiveFrom",
      width: 150,
      render: (v: string) => (v ? dayjs(v).format("DD MMM YYYY HH:mm") : "—"),
    },
    {
      title: "Diesel ₹/L",
      dataIndex: "dieselPricePerLitre",
      key: "diesel",
      align: "right" as const,
      width: 90,
      render: money,
    },
    {
      title: "Power ₹/kWh",
      dataIndex: "electricityRatePerKwh",
      key: "power",
      align: "right" as const,
      width: 100,
      render: money,
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      ellipsis: true,
      render: (v: string) => v || "—",
    },
    {
      title: "By",
      dataIndex: "createdByUserName",
      key: "by",
      width: 90,
      render: (v: string) => v || "—",
    },
    {
      title: "",
      key: "undo",
      width: 70,
      // Only the LATEST version can be removed — older history is immutable.
      render: (_: any, record: any, index: number) =>
        index === 0 ? (
          <Popconfirm
            title="Remove the latest card version?"
            description="The previous version comes back into force. Older history cannot be deleted."
            onConfirm={() => handleUndoLatest(record)}
            okText="Remove"
            cancelText="Keep"
          >
            <a href="#undo" onClick={(e) => e.preventDefault()}>
              Undo
            </a>
          </Popconfirm>
        ) : null,
    },
  ];

  return (
    // A drawer, not a modal: the card is long (energy + ten wages + defaults)
    // and is usually edited while looking at the fleet or a machine — sliding
    // in from the side keeps that context visible instead of dimming it.
    <Drawer
      title="Machinery Rate Parameters"
      open={open}
      onClose={onClose}
      width={680}
      destroyOnClose={false}
      footer={
        <div className="d-flex justify-content-end" style={{ gap: 8 }}>
          <Button color="light" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={saving || loading}
            onClick={handleSave}
          >
            {saving ? "Saving…" : "Save as New Version"}
          </Button>
        </div>
      }
    >
      {loading ? (
        <div className="text-center py-5">
          <Spin />
        </div>
      ) : (
        <>
          <div className="d-flex align-items-center mb-3" style={{ gap: 8 }}>
            <span className="text-soft">Scope</span>
            <Tag color={projectId > 0 ? "blue" : "default"}>
              {projectId > 0
                ? projectName || `Project ${projectId}`
                : "Organization (Master)"}
            </Tag>
            {resolvedFrom === "PROJECT" ? (
              <Tag color="green">Project card in force</Tag>
            ) : resolvedFrom === "ORG" ? (
              <Tag color={projectId > 0 ? "gold" : "green"}>
                {projectId > 0 ? "Using the organization card" : "Card in force"}
              </Tag>
            ) : (
              <Tag color="red">No card yet</Tag>
            )}
            {history.length > 0 && (
              <a
                href="#history"
                className="ms-auto"
                onClick={(e) => {
                  e.preventDefault();
                  setShowHistory((s) => !s);
                }}
                style={{ fontSize: 12 }}
              >
                <HistoryOutlined />{" "}
                {showHistory
                  ? "Hide history"
                  : `History (${history.length} version${history.length === 1 ? "" : "s"})`}
              </a>
            )}
          </div>

          <Alert
            type="info"
            showIcon
            className="mb-3"
            message="Prices are effective-dated"
            description="Saving adds a NEW version of this card from the date and time you pick below — it never rewrites the old one. Machine hours before that instant stay costed on the previous card; hours after it use the new prices. Back-dating recalculates history from that date."
          />

          {showHistory && (
            <div className="mb-3">
              <Table
                rowKey="rateParameterId"
                size="small"
                columns={historyColumns}
                dataSource={history}
                pagination={false}
                scroll={{ y: 220 }}
              />
            </div>
          )}

          {projectId > 0 && history.length === 0 && (
            <Alert
              type="info"
              showIcon
              className="mb-3"
              message="This project uses the organization card"
              description={
                orgCard
                  ? "Saving here starts a project chain that REPLACES the organization card for this project — it is not merged field by field. Start from the organization values if that is easier."
                  : "There is no organization card either. Whatever you enter here becomes this project's card."
              }
              action={
                orgCard ? (
                  <Button
                    size="sm"
                    color="light"
                    onClick={() => hydrate(orgCard)}
                  >
                    Copy org values
                  </Button>
                ) : null
              }
            />
          )}

          <h6 className="mb-2" style={{ color: "#022c57" }}>
            Effective from
          </h6>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label mb-1">Date &amp; time</label>
              <DatePicker
                value={effectiveFrom}
                onChange={(v) => setEffectiveFrom(v as Dayjs)}
                showTime={{ format: "HH:mm" }}
                format="DD MMM YYYY HH:mm"
                style={{ width: "100%" }}
                allowClear={false}
              />
              <div className="text-soft" style={{ fontSize: 11, marginTop: 2 }}>
                When these prices start applying. Defaults to now.
              </div>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label mb-1">Remarks</label>
              <Input
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Why the prices changed (e.g. diesel revision)"
                maxLength={500}
              />
            </div>
          </div>

          <h6 className="mb-2" style={{ color: "#022c57" }}>
            Energy
          </h6>
          <div className="row">
            {numberField(
              "dieselPricePerLitre",
              "Diesel Price",
              "Changing this re-rates fuel and lubricants for every diesel machine from the effective instant.",
              "₹ / litre"
            )}
            {numberField(
              "electricityRatePerKwh",
              "Electricity Rate",
              "Applies to every electric machine in this scope.",
              "₹ / kWh"
            )}
          </div>

          <h6 className="mb-2 mt-2" style={{ color: "#022c57" }}>
            Crew monthly wages
          </h6>
          <div className="row">
            {WAGE_FIELDS.map(([key, label, skill]) =>
              numberField(key as string, `${label} (${skill})`, undefined, "₹ / month")
            )}
          </div>

          <h6 className="mb-2 mt-2" style={{ color: "#022c57" }}>
            Defaults
          </h6>
          <div className="text-soft mb-2" style={{ fontSize: 12 }}>
            Used when a machine leaves the field blank. A value entered on the
            machine always wins.
          </div>
          <div className="row">
            {PERCENT_FIELDS.map(([key, label, hint]) =>
              numberField(key as string, label, hint, "%")
            )}
          </div>
        </>
      )}
    </Drawer>
  );
};

export default RateParametersDrawer;
